import { authApi, couponApi, freePointApi, merchantApi } from "@api";
import {
    PATH_COUPON,
    PATH_FREE_POINT,
    PATH_HISTORY_PUBLIC,
    PATH_HOME,
    PATH_LOGIN,
    PATH_TRAFFIC_SOURCE,
    PATH_WELCOME,
    authRoutes,
    enumLanguage,
    enumStatus,
    enumTypes,
    normalRoutes,
    publicRoutes,
} from "@configs";
import { ICoupon, IFreePoint, IMerchant, IMerchantQRcode, IRoute } from "@interfaces";
import { LayoutApp } from "@layouts";
import liff from "@line/liff";
import {
    getInfo,
    logout,
    resetAuth,
    resetLineToken,
    selectAuth,
    selectMerchant,
    setInfo,
    setLang,
    setLanguage,
    setLoading,
    setMerchant,
    setMerchantID,
    setMerchantPrefix,
    setMerchantSetting,
    setPopupHistory,
    setTrafficSourceExpired,
} from "@redux";
import { unwrapResult } from "@reduxjs/toolkit";
import fetchQuestions from "@Rocket-Innovation/components/app/crm/client/login/components/fetchQuestions";
import getLoginSettings from "@Rocket-Innovation/components/app/crm/client/login/components/getLoginSettings";
import Login from "@Rocket-Innovation/components/app/crm/client/login/components/Login";
import promptQuestions from "@Rocket-Innovation/components/app/crm/client/login/components/promptQuestions";
import getPostFormSettings from "@Rocket-Innovation/components/app/crm/client/post-form/components/getPostFormSettings";
import useContinueLogin from "@Rocket-Innovation/components/auth/hooks/useContinueLogin";
import { default as CacheStore } from "@Rocket-Innovation/components/store/CacheStore";
import UserStore from "@Rocket-Innovation/components/store/UserStore";
import UIStore from "@Rocket-Innovation/components/store/useUIStore";
import { getPrefix } from "@Rocket-Innovation/components/utilities/getPrefix";
import { formatMessage, splitMessage, useNotify } from "@utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { Route, useHistory, useLocation } from "react-router";
import PopupWarning from "src/Components/shared/PopupWarning";
import useRedirect from "src/Store/useRedirect";
import checkBlockMerchant from "src/Utils/checkBlockMerchant";
import { getDeployEnvironment } from "src/Utils/getDeployEnvironment";
import { getLiffId } from "src/Utils/getLiffId";
import getMerchant from "src/Utils/getMerchant";
import { deduplicate } from "src/Utils/requestDeduper";

const keyMerchantId = "merchant_id";
const keyHistoryId = "history_id";

export const ComponentAppRoute = (props: IRoute) => {
    //page props
    const { path, exact } = props;
    //page hook
    const location = useLocation();
    const pathname = location.pathname;
    const history = useHistory();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { error } = useNotify();
    //redux state
    const { auth, userInfo, merchant_prefix } = useSelector(selectAuth);
    const accessToken = auth ?? UserStore((s: any) => s.accessToken);
    const loginStatus = UserStore((s: any) => s.loginStatus);
    const { _id } = useSelector(selectMerchant);
    const merchant = useSelector(selectMerchant);
    //WHAT: check user login
    const Component = props.component;
    const Layout = LayoutApp;

    const { isMerchantReady, isReady, needUpdateInfo } = CacheStore();
    const [isWarning, setIsWarning] = useState<boolean>(false);

    //Handle login 2.0
    const [code, setCode] = useState<any>(false);

    const usePostFormSuccess = UIStore((state: any) => state.usePostFormSuccess);

    const signUpMethod = localStorage.getItem("signUpMethod");
    useContinueLogin({ code, Login });
    useEffect(() => {
        const queryString = window?.location.search;
        const urlParams = new URLSearchParams(queryString);
        setCode(urlParams.get("code")!);
        !accessToken && getLoginSettings();
        userInfo && getPostFormSettings();
    }, []);

    useEffect(() => {
        if (usePostFormSuccess) {
            dispatch(getInfo());
        }
    }, [usePostFormSuccess]);

    //Handle questions for new login 2.0
    useEffect(() => {
        handleLoginState();
        console.log("Login Status: ", loginStatus);
    }, [accessToken, userInfo, loginStatus]);

    const handleLoginState = async () => {
        if (accessToken && userInfo === null) {
            //Check if already registered
            const info = await authApi.getInfo();
            if (
                info.data.contact?.firstName &&
                info.data.contact?.lastName &&
                info.data.contact?.phoneNumber
            ) {
                CacheStore.setState({ formFields: [] });
            }

            //Close drawer if active
            if (info.data.status === enumStatus.ACTIVE) {
                dispatch(setInfo(info.data));
                UIStore.setState({
                    useDrawer: false,
                    useSubDrawer: false,
                    drawerContent: <></>,
                    subDrawerContent: <></>,
                });
                return;
            }
            fetchQuestions();
        } else if (
            accessToken &&
            userInfo &&
            !loginStatus &&
            userInfo.status === enumStatus.INACTIVE
        ) {
            console.log("AAAAA");
            promptQuestions();
        } else if (loginStatus === "success" && userInfo !== null) {
            UserStore.setState({ loginStatus: null });
            const info = await authApi.getInfo();

            dispatch(setInfo(info.data));
        }
        if (
            accessToken &&
            userInfo?.contact?.fullName &&
            userInfo?.contact?.lastName &&
            (userInfo?.contact?.phoneNumber || userInfo?.lineUserId)
        ) {
            const couponId = localStorage.getItem("couponId");
            const freepointId = localStorage.getItem("freepointId");
            if (couponId) {
                await couponApi
                    .receiveCoupon({
                        objectId: couponId,
                        status: enumStatus.ACTIVE,
                        type: enumTypes.COUPON,
                    })
                    .then(() => {
                        localStorage.removeItem("couponId");
                    })
                    .catch(() => {
                        localStorage.removeItem("couponId");
                    });
            }
            if (freepointId) {
                await freePointApi
                    .redeemedFreePoint(
                        {
                            objectId: freepointId,
                            status: enumStatus.ACTIVE,
                            type: enumTypes.FREE_POINT,
                        },
                        freepointId
                    )
                    .then(() => {
                        localStorage.removeItem("freepointId");
                    })
                    .catch(() => {
                        localStorage.removeItem("freepointId");
                    });
            }
        }
    };

    const fetchInfo = async () => {
        // const test = await merchantApi.getMerchantNew();
        // console.log("getMerchantNew => ", test);
        if (accessToken && !needUpdateInfo) {
            console.log("Get User Info");
            const info = await authApi.getInfo();
            dispatch(setInfo(info.data));

            //@ts-ignore
            unwrapResult(info);
            //@ts-ignore
            if (info?.data?.contact?.isFreeze) {
                setIsWarning(true);
            }
            if (info.data.status === enumStatus.INACTIVE) {
                // UIStore.setState({needUpdateInfo: true });
                // dispatch(
                //     openToast({
                //         message: t("page.need_update_info"),
                //         type: "warning",
                //         autoHideDuration: 2,
                //     })
                // );
                // history.push(PATH_SIGN_UP_FORM);
                //Logout for new login

                // localStorage.clear();
                // sessionStorage.clear();

                //Clear normal cache
                UserStore.setState({ accessToken: null, lineProfile: null });
                //Clear Line cache
                Object.keys(localStorage).forEach((key) => {
                    if (key.includes("LIFF_STORE")) {
                        localStorage.removeItem(key);
                    }
                });
                //Clear questions
                CacheStore.setState({ isLineCheck: false, formFields: [] });
                //Get login type
                !accessToken && (await getLoginSettings());
                userInfo && getPostFormSettings();

                if (liff.isInClient()) {
                    liff.logout();
                    // liff.closeWindow();
                }
                await dispatch(resetLineToken());
                await dispatch(resetAuth());
                await history.push(PATH_WELCOME);
            } else if (
                info?.data?.merchant?.memberTypeConfig?.isPreform &&
                info?.data?.contact?.memberTypeGroupNameId === undefined
            ) {
                console.log("Something went wrong on AppRoute");
                // history.push(PATH_SIGN_UP_FORM);
            }
            // } else if (!accessToken) {
            //     localStorage.clear();
            //     sessionStorage.clear();
            // if (liff.isInClient()) {
            //     liff.closeWindow();
            // }
            // await dispatch(resetLineToken());
            // await dispatch(resetAuth());
        }
    };

    const checkNormalRoute = () => {
        const isNormalRoute =
            pathname === PATH_HOME
                ? true
                : normalRoutes.some((item: string) => {
                      if (item === PATH_HOME) {
                          return false;
                      }
                      return pathname.includes(item);
                  });

        if (isNormalRoute) {
            // alert(
            //     `_id = ${_id ? "true" : "false"} accessToken = ${
            //         accessToken ? "true" : "false"
            //     } DISABLE_APP_ROUTE = ${sessionStorage.getItem(
            //         "DISABLE_APP_ROUTE"
            //     )} LINE_LOGIN_SUCCESS = ${localStorage.getItem("LINE_LOGIN_SUCCESS")}`
            // );
            if (!_id) {
                checkMerchantPrefix();
            }
            if (!_id && !accessToken) {
                checkMerchantPrefix();
            }
            if (!accessToken && _id && sessionStorage.getItem("DISABLE_APP_ROUTE") !== "true") {
                console.log("replacing in AppRoute > A");
                // return history.push(PATH_SIGN_IN);
            }

            if (!_id && !accessToken) {
                console.log("replacing in AppRoute > B");
                return history.push(PATH_WELCOME);
            }
            if (!_id) {
                console.log("replacing in AppRoute > C");
                return history.push(PATH_WELCOME);
            }
        }
    };

    const checkAuthRoute = () => {
        const isAuthRoute = authRoutes.some((item: string) => pathname.includes(item));

        if (isAuthRoute) {
            if (auth && userInfo?.status === enumStatus.ACTIVE) {
                if (merchant?.memberTypeConfig?.isPreform) {
                    if (
                        userInfo?.contact?.memberTypeGroupNameId !== undefined &&
                        userInfo?.contact?.memberTypeGroupNameId !== ""
                    ) {
                        if (!(pathname.includes("/free-point/") || pathname.includes("/coupon/"))) {
                            // history.push(PATH_HOME);
                        }
                    }
                }
                //old login
                // else {
                //     if (!(pathname.includes("/free-point/") || pathname.includes("/coupon/"))) {
                //         history.push(PATH_HOME);
                //     }
                // }
            } else if (!_id) {
                // alert("no merchant id");
            }
        }
    };

    // const setInitMerchant = async () => {
    //     try {
    //         const merchantConfig = await merchantApi.getMerchantNew();

    //         const merchant = merchantConfig.data.data as IMerchant;
    //         if (merchant) {
    //             if (!merchant.enableAccess) {
    //                 // alert("merchant enableAccess false");
    //             } else {
    //                 dispatch(setMerchant(merchant));
    //                 // dispatch(setMainColor(merchant.themeColor));
    //             }
    //         }
    //     } catch {
    //         setMerchantID("");
    //         // alert("can't get merchant");
    //     }
    // };
    const setInitMerchant = async () => {
        await dispatch(getMerchant());
        await dispatch(setMerchantSetting());
    };

    const getMerchantByCouponId = async (id: string) => {
        try {
            dispatch(setLoading(true));
            const res = await deduplicate(`coupon-${id}`, () => merchantApi.getCouponId(id));
            const data = res.data as ICoupon;
            dispatch(setMerchantID(data.merchantId));
            await setInitMerchant();
        } catch {
            // alert("can't get merchant by coupon id");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const getMerchantByFreePointId = async (id: string) => {
        // alert(`getMerchantByFreePointId ${id}`);

        try {
            dispatch(setLoading(true));
            const res = await merchantApi.getFreePointId(id);
            const data = res.data as IFreePoint;
            dispatch(setMerchantID(data.merchantId));
            await setInitMerchant();
        } catch (err: any) {
            if (err.response) {
                error(formatMessage(err.response));
            }
            // alert("can't get merchant by free point id");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const getMerchantByTrafficSource = async (id: string) => {
        // ponytail: dead/disabled QR blocks on the page instead of silently landing on /welcome
        let isExpired = false;
        try {
            const res = await merchantApi.getTrafficSourceId(id);
            const data = res.data as IMerchantQRcode;
            if (data.status !== enumStatus.ACTIVE) {
                isExpired = true;
                return;
            }
            dispatch(setMerchantID(data.merchantId));
            await setInitMerchant();
        } catch (error: any) {
            // 4xx = the QR itself is gone/invalid; 5xx or network = transient, keep old behaviour
            const httpStatus = error?.response?.status;
            isExpired = httpStatus >= 400 && httpStatus < 500;
        } finally {
            dispatch(setTrafficSourceExpired(isExpired));
            if (
                !isExpired &&
                !(pathname.includes("/free-point/") || pathname.includes("/coupon/"))
            ) {
                if (!isMobile) {
                    localStorage.clear();
                    Object.keys(sessionStorage).forEach((key) => {
                        if (key !== "redirectCache") {
                            sessionStorage.removeItem(key);
                        }
                    });
                    if (liff.isInClient()) {
                        liff.logout();
                    }
                }

                history.push(PATH_WELCOME);
            }
        }
    };

    const getMerchantByPublicHistory = async () => {
        const searchParams = new URLSearchParams(window.location.search);
        const merchantId = searchParams.get(keyMerchantId);
        const historyId = searchParams.get(keyHistoryId);
        if (merchantId && historyId) {
            try {
                dispatch(setMerchantID(merchantId));
                await setInitMerchant();
            } catch {
                // alert("can't get merchant by Public History");
            } finally {
                if (!(pathname.includes("/free-point/") || pathname.includes("/coupon/"))) {
                    dispatch(setPopupHistory(true));
                    localStorage.clear();
                    Object.keys(sessionStorage).forEach((key) => {
                        if (key !== "redirectCache") {
                            sessionStorage.removeItem(key);
                        }
                    });
                    if (liff.isInClient()) {
                        liff.logout();
                    }

                    history.push(PATH_WELCOME);
                }
            }
        }
    };

    const checkPublicRoute = () => {
        const pathname = window.location.pathname;
        const isPublicRoute = publicRoutes.some((item: string) => pathname.includes(item));

        if (isPublicRoute) {
            const pathArr = pathname.substring(1).split("/", 2);
            const currentPath = "/" + pathArr[0];
            const id = pathArr[1] || undefined;

            if (id) {
                switch (currentPath) {
                    case PATH_COUPON: {
                        getMerchantByCouponId(id);
                        break;
                    }
                    case PATH_FREE_POINT: {
                        getMerchantByFreePointId(id);
                        break;
                    }
                    case PATH_TRAFFIC_SOURCE: {
                        getMerchantByTrafficSource(id);
                        break;
                    }
                    default:
                        break;
                }
            } else {
                if (currentPath === PATH_HISTORY_PUBLIC) {
                    getMerchantByPublicHistory();
                }
            }
        }
    };

    const fetchLang = async () => {
        const lang = localStorage.getItem("lang");
        const countrySetting = localStorage.getItem("countrySetting");
        if (_id && (!lang || !countrySetting)) {
            let resLang = await merchantApi.getCountrySetting({
                merchantId: _id,
            });
            if (resLang.data.error === "0") {
                dispatch(setLang(resLang?.data?.result?.countrySetting));
                dispatch(setLanguage(resLang?.data?.result?.countrySetting?.language));
                if (resLang?.data?.result?.countrySetting?.language === "THAI") {
                    i18n.changeLanguage(enumLanguage.TH);
                } else {
                    i18n.changeLanguage(enumLanguage.EN);
                }
            }
        } else if (lang) {
            dispatch(setLanguage(lang));
            if (lang === "THAI") {
                i18n.changeLanguage(enumLanguage.TH);
            } else {
                i18n.changeLanguage(enumLanguage.EN);
            }
        }
    };

    const checkMerchantPrefix = async () => {
        const hostname = window.location.hostname;
        const prefix = hostname.split(".")[0];
        // if not have prefix on browser or if there are different merchnat
        // call mrechantconfig prefix
        if (
            (!merchant_prefix || merchant_prefix !== prefix) &&
            !(pathname.includes("/free-point/") || pathname.includes("/coupon/"))
        ) {
            try {
                dispatch(setMerchantPrefix(prefix));
                await setInitMerchantPrefix();
            } catch (e) {
                // if something is wrong, go to 404
                // alert(`check merchant prefix error`);
            } finally {
                // history.push(PATH_WELCOME);
            }
        }
    };

    const setInitMerchantPrefix = async () => {
        try {
            const res = await merchantApi.getMerchantNew();
            const merchant = res.data.data as IMerchant;
            if (merchant) {
                dispatch(setMerchant(merchant));
                dispatch(setMerchantSetting());
                // dispatch(setMainColor(merchant.themeColor));
                // const index = configClient.findIndex(
                //     (item) => item.merchantId === merchant._id
                // );
                // if (index >= 0) {
                //     dispatch(setAuth(configClient[index].token));
                // } else dispatch(logout());
                // after get merchant id=> go to welcome page
                if (!(pathname.includes("/free-point/") || pathname.includes("/coupon/"))) {
                    // localStorage.clear();
                    Object.keys(localStorage).forEach((key) => {
                        if (
                            key !== "couponId" &&
                            key !== "freepointId" &&
                            key !== "inviteId" &&
                            key !== "sellerId"
                        ) {
                            localStorage.removeItem(key);
                        }
                    });
                    Object.keys(sessionStorage).forEach((key) => {
                        if (key !== "redirectCache") {
                            sessionStorage.removeItem(key);
                        }
                    });
                    if (liff.isInClient()) {
                        liff.logout();
                    }

                    //Everyone can use this public path
                    if (pathname === PATH_LOGIN) return;

                    history.push(PATH_WELCOME);
                }
            }
        } catch {
            setMerchantID("");
        }
    };

    const syncCheck = async () => {
        console.log("isReady", isReady);
        if (!isReady) return;
        // const pathname = window.location.pathname;
        // const pathArr = pathname.substring(1).split("/", 2);
        // const currentPath = "/" + pathArr[0];
        // alert(currentPath);

        // get info
        await fetchInfo();
        // check prefix and get merchat info
        checkMerchantPrefix();
        // logic public route to get merchant
        checkPublicRoute();
        // //check normal route
        checkNormalRoute();
        // check auth route
        checkAuthRoute();
        fetchLang();
    };

    const redirect = async () => {
        //For Redirect
        const params = new URLSearchParams(window.location.search);
        const { app, store_id } = useRedirect.getState();
        let currentApp = app;
        let currentStoreId = store_id;

        if (!currentApp || !currentStoreId) {
            const app_param = params.get("app")!;
            const store_id_param = params.get("store_id")!;
            useRedirect.setState({ app: app_param, store_id: store_id_param });

            currentApp = app_param;
            currentStoreId = store_id_param;
        }

        if (accessToken && currentApp && currentStoreId && merchant) {
            const info = await authApi.getInfo();

            dispatch(setInfo(info.data));
            if (info.data.status !== enumStatus.ACTIVE) return;

            const env = getDeployEnvironment();
            const lang = (merchant as any).countrySetting.flag;

            let redirectTo = "";
            switch (currentApp) {
                case "userorder":
                    redirectTo = `https://${
                        env === "production" ? "pos" : "uat-pos"
                    }-client-app.rocket-tech.app/auth/${accessToken}/${
                        merchant._id
                    }/${currentStoreId}`;
                    break;
                case "bigcommerce":
                    redirectTo = `${getPrefix()}.rocket-ecommerce.${
                        env === "production" ? "tech" : "app"
                    }/auth/${accessToken}?lang=${lang}`;
                    break;
            }

            if (redirectTo) {
                useRedirect.setState({ app: null, store_id: null });
                window.location.replace(redirectTo);
                return;
            }
        }
    };

    useEffect(() => {
        if (merchant) {
            if (!signUpMethod) {
                localStorage.setItem("signUpMethod", merchant.signUpSettings?.signUpMethod || "");
            }
            if (signUpMethod) {
                if (signUpMethod !== merchant.signUpSettings?.signUpMethod) {
                    localStorage.clear();
                    window.location.reload();
                }
            }
        }
        if (isMerchantReady && !isReady) {
            const id = getLiffId(merchant as IMerchant);
            console.log("LIFF ID", id);
            // if (id !== "skipLine") {
            //     liff.init({ liffId: id })
            //         .then(() => {
            //             CacheStore.setState({ isReady: true });
            //         })
            //         .catch((err) => {
            //             console.log("ERR", err);
            //         });
            // } else {
            //     CacheStore.setState({ isReady: true });
            // }
            CacheStore.setState({ isReady: true });
        } else if (!isReady) {
            retry();
        }
    }, [liff, merchant, isMerchantReady]);

    useEffect(() => {
        if (isMerchantReady && liff.isInClient()) {
            const id = getLiffId(merchant as IMerchant);
            if (id !== "skipLine") {
                liff.init({ liffId: id })
                    .then(() => {
                        CacheStore.setState({ isReady: true });
                    })
                    .catch((err) => {
                        console.log("ERR", err);
                    });
            }
        }
    }, [merchant, isMerchantReady]);

    const retry = async () => {
        await getMerchant().then(() => {
            dispatch(setMerchantSetting());
        });
    };
    useEffect(() => {
        //FOR Blocking
        if (checkBlockMerchant()) return;

        redirect();

        syncCheck();
    }, [isReady]);

    const handleCloseWarning = () => {
        dispatch(logout());

        sessionStorage.clear();
        localStorage.clear();
        setIsWarning(false);
        history.replace("/");
    };

    const isPublicRoute = publicRoutes.some((item: string) => pathname.includes(item));
    //Check if user is login.
    if (userInfo === null && pathname !== "/" && !isPublicRoute) {
        console.log("replacing in AppRoute");
        history.replace("/");
        CacheStore.setState({ formFields: [] });

        if (accessToken) {
            UserStore.setState({ accessToken: null });
        } else {
            UIStore.setState({
                useDrawer: true,
                drawerContent: (
                    <span
                        style={{
                            color: "black",
                        }}
                    >
                        <Login />
                    </span>
                ),
            });
        }

        return <></>;
    }

    return (
        <Route
            path={path}
            exact={exact}
            render={() => (
                <div>
                    <Layout>
                        <Component />
                    </Layout>
                    {isWarning && (
                        <PopupWarning
                            onClose={handleCloseWarning}
                            text={splitMessage(
                                "บัญชีของคุณมีปัญหา โปรดติดต่อผู้ดูแลระบบ || Your account has an issue, please contact the admin."
                            )}
                        />
                    )}
                </div>
            )}
        />
    );
};
